import { useState, useEffect } from 'react';
import ProfileSidebar from '../../components/Profile/ProfileSidebar';
import ProfileMobileSubNav from '../../components/Profile/ProfileMobileSubNav';
import FallbackBanner from '../../assets/banner-1.svg';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaClock, FaTimesCircle, FaCheckCircle, FaChevronRight, FaShoppingCart } from 'react-icons/fa';
import { useUser } from '../../contexts/user';
import { useCart } from '../../contexts/cart';
import { FiX } from 'react-icons/fi';
import log from '../../utils/logger';
import api from '../../api/api';
import useToast from '../../hooks/useToast';
import Toast from '../../components/Toast';

const Order = () => {
  const { t, i18n } = useTranslation();
  const { toastMessage, toastType, showToast } = useToast();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isBouncing, setIsBouncing] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(null);
  const { idToken, logout, adminImpersonate, user } = useUser();
  const { setCart, clearCart, cartItems, cartId } = useCart();

  const formatMoney = (value) => Number(value || 0).toFixed(2);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await api.get('/buyer/order/all', {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
          params: {
            adminImpersonateToken: adminImpersonate || null,
            buyerEmail: user?.email,
            buyerId: user?.id,
          },
        });
        setOrders(response?.data?.response?.data || []);
      } catch (err) {
        log.error('Error fetching orders:', err);
        setError(t('ordersLoadError'));
        const message = err.response?.data?.error || '';
        if (message === 'TOKEN_EXPIRED' || message === 'INVALID_ID_TOKEN') {
          navigate('/home');
          sessionStorage.setItem('sessionExpired', 'true');
          sessionStorage.setItem(
            'toast',
            JSON.stringify({
              message: t('sessionExpiredMessage'),
              type: 'error',
            }),
          );
          logout();
          clearCart();
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [idToken]);

  useEffect(() => {
    if (showReorderModal) {
      requestAnimationFrame(() => {
        setIsModalVisible(true);
      });
    }
    document.body.style.overflow = showReorderModal ? 'hidden' : 'auto';
  }, [showReorderModal]);

  const handleReorder = (order) => {showToast(t('sessionExpiredMessage'), 'error');return;
    if (cartId && cartItems.length > 0) {
      setPendingOrder(order);
      setShowReorderModal(true);
      return;
    }

    handleReorderConfirmed(order);
  };

  const handleReorderConfirmed = async (order) => {
    try {
      const res = await api.post(
        `/buyer/order/reorder/${order._id}`,
        {
          adminImpersonateToken: adminImpersonate || null,
          buyerEmail: user?.email,
          buyerId: user?.id,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        },
      );

      const { cartItems = [], cartId = null, skippedItems = [] } = res.data.response?.data ?? {};

      setCart(cartItems, cartId);

      if (skippedItems && skippedItems.length > 0) {
        showToast(t('skippedReorderItemsMessage'), 'warning');
        log.log('Skipped items:', skippedItems);
      }

      if (cartItems && cartItems.length > 0){
        navigate('/checkout');
      }
    } catch (err) {
      log.error('Reorder failed:', err);

      const message = err.response?.data?.error || '';

      if (message === 'TOKEN_EXPIRED' || message === 'INVALID_ID_TOKEN') {
        navigate('/home');
        sessionStorage.setItem('sessionExpired', 'true');
        sessionStorage.setItem(
          'toast',
          JSON.stringify({
            message: t('sessionExpiredMessage'),
            type: 'error',
          }),
        );
        logout();
        clearCart();
      } else {
        showToast(t('ordersLoadError'), 'error');
      }
    }
  };

  const confirmReorder = () => {
    setShowReorderModal(false);
    handleReorderConfirmed(pendingOrder);
    setPendingOrder(null);
  };

  const cancelReorder = () => {
    setIsModalVisible(false);

    setTimeout(() => {
      setShowReorderModal(false);
    }, 250);
  };

  const bounceModal = () => {
    setIsBouncing(false);

    requestAnimationFrame(() => {
      setIsBouncing(true);
    });

    setTimeout(() => {
      setIsBouncing(false);
    }, 50);
  };

  if ((!idToken || !user) && !adminImpersonate) {
    return (
      <>
        <div className="flex flex-col min-h-dvh">
          <Header />
          <ProfileMobileSubNav />
          <section className="pt-20 lg:px-3 pb-20 lg:pb-40">
            <div className="max-w-[1220px] w-full mx-auto flex gap-4 lg:gap-20">
              <div className="hidden lg:block">
                <ProfileSidebar />
              </div>
              <div className="flex-1 flex justify-center items-center">
                <p className="text-[#999]">{t('loginRequiredMessage')}</p>
              </div>
            </div>
          </section>
        </div>
        <Footer />
        <Toast message={toastMessage} type={toastType} />
      </>
    );
  }

  if (loading) {
    return (
      <>
        <div className="flex flex-col min-h-dvh">
          <Header />
          <ProfileMobileSubNav />
          <section className="pt-20 lg:px-3 pb-20 lg:pb-40">
            <div className="max-w-[1220px] w-full mx-auto flex gap-4 lg:gap-20">
              <div className="hidden lg:block">
                <ProfileSidebar />
              </div>
              <div className="flex-1 flex-col flex gap-2 p-2">
                {Array.from({ length: 3 }).map((_, idx) => (
                  <div key={idx} className="rounded-xl border border-gray-200 p-4 flex flex-col space-y-4 bg-white animate-pulse">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded bg-gray-200"></div>
                      <div className="flex-1 flex flex-col gap-2">
                        <div className="h-4 w-32 bg-gray-200 rounded"></div>
                        <div className="h-3 w-48 bg-gray-100 rounded"></div>
                      </div>
                      <div className="h-4 w-16 bg-gray-200 rounded"></div>
                    </div>
                    <div className="space-y-2 border-t border-gray-100 pt-3">
                      <div className="h-3 w-full bg-gray-100 rounded"></div>
                      <div className="h-3 w-3/4 bg-gray-100 rounded"></div>
                      <div className="h-3 w-1/2 bg-gray-100 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
        <Footer />
        <Toast message={toastMessage} type={toastType} />
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="flex flex-col min-h-dvh">
          <Header />
          <ProfileMobileSubNav />
          <section className="pt-20 lg:px-3 pb-20 lg:pb-40">
            <div className="max-w-[1220px] w-full mx-auto flex gap-4 lg:gap-20">
              <div className="hidden lg:block">
                <ProfileSidebar />
              </div>
              <div className="flex-1 flex justify-center items-center">
                <p className="text-red-500">{error}</p>
              </div>
            </div>
          </section>
        </div>
        <Footer />
        <Toast message={toastMessage} type={toastType} />
      </>
    );
  }

  if (!orders.length) {
    return (
      <>
        <div className="flex flex-col min-h-dvh">
          <Header />
          <ProfileMobileSubNav />
          <section className="pt-20 lg:px-3 pb-20 lg:pb-40">
            <div className="max-w-[1220px] w-full mx-auto flex gap-4 lg:gap-20">
              <div className="hidden lg:block">
                <ProfileSidebar />
              </div>
              <div className="flex-1 flex justify-center items-center">
                <p className="text-[#999]">{t('You have no orders yet.')}</p>
              </div>
            </div>
          </section>
        </div>
        <Footer />
        <Toast message={toastMessage} type={toastType} />
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col min-h-dvh">
        <Header />
        <ProfileMobileSubNav />
        <section className="lg:pt-20 lg:px-3 lg:pb-40">
          <div className="max-w-[1220px] w-full mx-auto flex gap-4 lg:gap-20 items-start">
            <div className="hidden lg:block">
              <ProfileSidebar />
            </div>

            <div className="flex-1 flex-col flex gap-2 p-2 bg-gray-300 lg:rounded-xl border border-[#E6E6E6]">
              {orders.map((order, index) => {
                const store = order?.store;
                const logo = store?.logoImage || FallbackBanner;
                const companyName = store?.storeName || t('unknownMerchant');
                const cartItems = order?.cartItems || [];
                const totalTaxes = order?.taxAmount ?? 0;
                const couponDiscount = Array.isArray(order?.discountedItems)
                  ? order.discountedItems.reduce((sum, item) => sum + Number(item?.discount || 0), 0)
                  : 0;
                const subtotalBeforeDiscount = Number(order?.subtotal || 0);
                const hasCouponDetails = Boolean(order?.coupon && order?.coupon?.code);

                return (
                  <section
                    key={order?._id || index}
                    aria-label={`Commande ${companyName}`}
                    className="rounded-xl border border-gray-200 p-4 flex flex-col space-y-4 bg-white"
                  >
                    <header
                      onClick={() =>
                        navigate('/profile/track-order', {
                          state: {
                            order,
                            orderNumber: order?.orderNumber,
                            timestamp: order?.createdAt,
                            store: order?.store,
                          },
                        })
                      }
                      className="flex items-center space-x-3 cursor-pointer"
                    >
                      <img
                        src={logo}
                        alt={`Logo de ${companyName}`}
                        className="w-10 h-10 rounded object-cover"
                      />
                      <div className="flex-1 text-sm font-normal text-gray-700 flex flex-col gap-0.5">
                        <span className="font-semibold text-black whitespace-nowrap">
                          {companyName}
                        </span>
                        <div className="flex flex-wrap items-center gap-2 text-xs font-normal text-[#777]">
                          <span
                            className={`flex items-center space-x-1 ${
                              order.orderStatus === 'accepted'
                                ? 'text-yellow-500'
                                : 'text-red-600'
                            }`}
                          >
                            {order.orderStatus === 'completed' && (
                              <FaCheckCircle className="text-xs text-green-500" />
                            )}
                            {order.orderStatus === 'canceled' && (
                              <FaTimesCircle className="text-xs text-red-600" />
                            )}
                            {order.orderStatus !== 'completed' &&
                              order.orderStatus !== 'canceled' && (
                              <FaClock className="text-xs text-yellow-500" />
                            )}
                            <span className="text-[#777]">
                              {t('Order')} #{order?.orderNumber ?? 'N/A'}
                            </span>
                          </span>
                          <span>|</span>
                          <span>
                            {new Date(order.createdAt).toLocaleString()}
                          </span>
                          <span>| {t(order.orderType)}</span>
                        </div>
                      </div>
                      <div className="font-bold text-black text-sm whitespace-nowrap">
                        {(order?.grandTotal ?? 0).toFixed(2)}$
                      </div>
                      <FaChevronRight className="text-black text-sm" />
                    </header>

                    <table className="w-full text-left text-black text-xs border-t border-gray-200 table-fixed">
                      <thead>
                        <tr className="text-xs font-bold border-b border-gray-200">
                          <th className="py-2 px-1 w-auto">{t('PRODUCT')}</th>
                          <th className="py-2 px-1 w-12 text-center whitespace-nowrap">
                            {t('QTY')}
                          </th>
                          <th className="py-2 px-1 w-20 text-right pr-2 whitespace-nowrap">
                            {t('PRICE')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {cartItems.map((product, idx) => (
                          <tr
                            key={idx}
                            className="border-b border-gray-200 align-top"
                          >
                            <td className="py-2 px-1 text-[#777]">
                              <div className="flex flex-col">
                                <span className="font-semibold">
                                  {i18n.language === 'en'
                                    ? product?.productEnName
                                    : product?.productFrName}
                                </span>
                                {product?.variantId != null && (
                                  <>
                                    <span className="font-semibold text-black ml-3 mt-1">
                                      {i18n.language === 'en'
                                        ? product.variantEnTitle
                                        : product.variantFrTitle}
                                    </span>
                                    <span className="text-xs ml-3 mt-1">
                                      {i18n.language === 'en'
                                        ? product.variantEnName
                                        : product.variantFrName}
                                    </span>
                                  </>
                                )}
                              </div>
                            </td>
                            <td className="py-2 px-1 text-center text-[#777] align-top">
                              {product?.quantity ?? '-'}
                            </td>
                            <td className="py-2 px-1 text-right text-[#777] pr-2 align-top">
                              {(product?.price ?? 0).toFixed(2)}$
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td colSpan={2} className="py-1 px-1 text-xs text-[#333] text-right">
                            {t('Subtotal')}
                          </td>
                          <td className="py-1 px-1 text-xs text-[#333] text-right pr-2">
                            {formatMoney(subtotalBeforeDiscount)}$
                          </td>
                        </tr>

                        {hasCouponDetails && (
                          <tr>
                            <td colSpan={2} className="py-1 px-1 text-xs text-green-600 text-right">
                              {t('Coupon discount', { defaultValue: 'Coupon discount' })}
                              {order?.coupon?.code ? ` (${order.coupon.code})` : ''}
                            </td>
                            <td className="py-1 px-1 text-xs text-green-600 text-right pr-2">
                              -{formatMoney(couponDiscount)}$
                            </td>
                          </tr>
                        )}

                        <tr>
                          <td colSpan={2} className="py-1 px-1 text-xs text-[#333] text-right">
                            {t('taxes')}
                          </td>
                          <td className="py-1 px-1 text-xs text-[#333] text-right pr-2">
                            {formatMoney(totalTaxes)}$
                          </td>
                        </tr>

                        <tr>
                          <td colSpan={2} className="py-1 px-1 text-xs font-semibold text-black text-right">
                            {t('grandTotal')}
                          </td>
                          <td className="py-1 px-1 text-xs font-semibold text-black text-right pr-2">
                            {formatMoney(order?.grandTotal ?? 0)}$
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <div className="flex justify-between items-center px-2">
                      <div className="ml-4">
                        <button className="hidden bg-gray-100 text-black text-xs font-semibold px-6 py-2 rounded">
                          {t('Review order')}
                        </button>
                      </div>
                      {/* Reorder */}
                      <div className="mr-4">
                        {['completed', 'canceled'].includes(
                          order?.orderStatus,
                        ) ? (
                            <button
                              onClick={() => handleReorder(order)}
                              className="bg-black text-white text-xs font-semibold px-6 py-2 rounded flex items-center space-x-2"
                            >
                              <FaShoppingCart className="text-xs" />
                              <span>{t('Re-order')}</span>
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                navigate('/profile/track-order', {
                                  state: {
                                    order,
                                    orderNumber: order?.orderNumber,
                                    timestamp: order?.createdAt,
                                    store: order?.store,
                                  },
                                })
                              }
                              className="bg-black text-white text-xs font-semibold px-6 py-2 rounded"
                            >
                              {t('Track order')}
                            </button>
                          )}
                      </div>
                    </div>
                  </section>
                );
              })}
            </div>
          </div>
        </section>
      </div>
      <Footer />
      <Toast message={toastMessage} type={toastType} />

      {showReorderModal && (
        <div className="fixed left-0 right-0 bottom-0 top-0 lg:top-[65px] z-40 flex items-center justify-center">
          {/* Overlay */}
          <div
            onClick={bounceModal}
            className={`
              absolute inset-0 bg-[#212121] bg-opacity-[0.46]
              transition-opacity duration-250
              ${isModalVisible ? 'opacity-100' : 'opacity-0'}
            `}
          />

          {/* Modal */}
          <div
            className={`
              relative z-50
              bg-white rounded-2xl shadow-xl
              w-[90%] max-w-md
              p-6
              transform-gpu
              transition-all duration-250 ease-out
              ${
        isModalVisible
          ? isBouncing
            ? 'opacity-100 scale-105 translate-y-0'
            : 'opacity-100 scale-100 translate-y-0'
          : 'opacity-0 scale-95 translate-y-2'
        }
            `}
          >
            <div className="text-left">
              {/* Close button */}
              <button
                onClick={cancelReorder}
                className="flex absolute top-3 right-3 w-6 h-6 items-center justify-center rounded-full bg-[#eee] hover:bg-[#ddd] border border-[#ddd] transition"
              >
                <FiX className="text-[#666] w-5 h-5" />
              </button>

              <h2 className="text-lg font-semibold lg:mt-3 mb-3">
                {t('replaceCartItems')}
              </h2>

              <p className="text-sm text-[#666] mb-6">
                {t('replaceCartItemsMessage')
                  .split('\n')
                  .map((line, i) => (
                    <span key={i}>
                      {line}
                      <br />
                    </span>
                  ))}
              </p>

              <div className="flex flex-col lg:flex-row justify-between gap-3">
                <button
                  onClick={cancelReorder}
                  className="px-18 lg:px-20 py-2 lg:py-3 rounded-lg bg-[#eee] border border-gray-300 hover:bg-gray-100 transition"
                >
                  {t('no')}
                </button>

                <button
                  onClick={confirmReorder}
                  className="px-18 lg:px-20 py-2 lg:py-3 rounded-lg bg-black text-white hover:opacity-90 transition"
                >
                  {t('yes')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Order;
